import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import { LoadingContainer } from '@pflegenavi/web-components';
import { getGlobalServiceProviderIdFromToken } from '../../utils/getGlobalServiceProviderIdFromToken';
import {
  useCreateServiceProviderStripeAccount,
  useServiceProviderStripeAccount,
} from '@pflegenavi/service-provider-frontend/api';
import { useNavigate } from 'react-router-dom';
import { spAppMainPages } from '@pflegenavi/frontend/routing';
import * as Sentry from '@sentry/react';
import { useAuthentication } from '@pflegenavi/frontend/authentication';

export const ServiceProviderStripeOnboardingRefresh: FC = () => {
  // @ts-expect-error // getTokenImmediate is intentionally hidden. It is necessary here to extract the globalServiceProviderId
  const { getTokenImmediate } = useAuthentication();
  const globalServiceProviderId = getGlobalServiceProviderIdFromToken(
    getTokenImmediate()
  );
  const navigate = useNavigate();

  const { data: stripeAccount } = useServiceProviderStripeAccount({
    globalServiceProviderId,
  });
  const { mutateAsync: createStripeAccount } =
    useCreateServiceProviderStripeAccount();

  const startOnboarding = useCallback((url: string) => {
    window.location.href = url;
  }, []);

  const createAccount = useCallback(async () => {
    if (!globalServiceProviderId) {
      return;
    }

    const account = await createStripeAccount({ globalServiceProviderId });
    startOnboarding(account.url);
  }, [createStripeAccount, globalServiceProviderId, startOnboarding]);

  const stripeActive =
    stripeAccount?.stripeAccountId &&
    stripeAccount.accountOnboardingStatus === 'complete'
      ? stripeAccount
      : undefined;

  const stripePending =
    stripeAccount?.stripeAccountId &&
    stripeAccount.accountOnboardingStatus === 'pending'
      ? stripeAccount
      : undefined;

  useEffect(() => {
    if (stripeAccount && stripeAccount.stripeAccountId === undefined) {
      // Somehow the account was not created, so we need to create it.
      // This should actually never be the case, because to get here, the account should already
      // be created
      createAccount().catch(Sentry.captureException);
    } else if (stripePending) {
      startOnboarding(stripePending.url);
    } else if (stripeActive) {
      // The account is already active, so we can redirect to the profile page.
      navigate(spAppMainPages.PROFILE);
    }
  }, [
    createAccount,
    navigate,
    startOnboarding,
    stripeAccount,
    stripeActive,
    stripePending,
  ]);

  return <LoadingContainer />;
};

export default ServiceProviderStripeOnboardingRefresh;
